<template>
  <section>
    <b-card-actions
      :show-loading="loading"
      action-collapse
      :title="$t('Configuração do Programa de Recompensas')"
    >
      <FormulateForm
        ref="rewardForm"
        name="rewardForm"
        @submit="saveConfiguration"
      >
        <b-row>
          <b-col md="4">
            <FormulateInput
              id="reward_config-is_active"
              v-model="configuration.isActive"
              type="switch"
              :label="$t('Sistema de recompensas ativo?')"
            />
          </b-col>
          <b-col
            v-if="configuration.isActive"
            md="4"
          >
            <FormulateInput
              id="reward_config-is_RedemptionActive"
              v-model="configuration.isRedemptionActive"
              type="switch"
              :label="$t('Resgate ativo?')"
            />
          </b-col>
        </b-row>

        <template v-if="configuration.isActive">
          <b-row>
            <b-col md="4">
              <FormulateInput
                id="reward_config-global_cashback_percentage"
                v-model="configuration.globalCashbackPercentage"
                type="number"
                :label="$t('Percentual Global de Cashback (%)')"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                id="reward_config-global_credit_validity_days"
                v-model="configuration.globalCreditValidityDays"
                type="number"
                :label="$t('Validade dos Créditos (dias)')"
                :min="1"
                validation="required|min:1"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="4">
              <FormulateInput
                id="reward_config-global_credit_usage_limit_percentage"
                v-model="configuration.globalCreditUsageLimitPercentage"
                type="number"
                :label="$t('Limite Global de Uso por Venda (%)')"
                :help="$t('Percentual máximo do valor da venda que pode ser pago com ecash. Vale para as tabelas de preço sem configuração própria abaixo.')"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col
              v-show="false"
              md="6"
            >
              <FormulateInput
                id="reward_config-minimum_purchase_value"
                v-model="configuration.minimumPurchaseValue"
                type="text-number"
                currency="R$"
                :precision="2"
                validation="optional|min:1"
                :label="$t('Valor Mínimo de Compra')"
                :help="$t('Deixe em branco para não ter valor mínimo')"
              />
            </b-col>
            <b-col md="6">
              <FormulateInput
                id="reward_config-require_app_registration"
                v-model="configuration.requireAppRegistration"
                type="switch"
                :label="$t('Exigir cadastro no app?')"
                :instruction="
                  $t(
                    'Quando habilitado, o cliente deve estar cadastrado no aplicativo para receber recompensas'
                  )
                "
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <FormulateInput
                id="reward_config-enabled_sale_classifications"
                v-model="configuration.enabledSaleClassifications"
                type="vue-select"
                :label="$t('Classificações de Venda Habilitadas')"
                :options="saleClassificationOptions"
                :multiple="true"
                :clearable="false"
                :placeholder="$t('Selecione as classificações')"
                validation="required"
                validation-behavior="live"
              />
            </b-col>
            <!-- <b-col md="6">
              <FormulateInput
                id="reward_config-enabled_price_tables"
                v-model="configuration.enabledPriceTables"
                type="vue-select"
                :label="$t('Tabelas de Preço Habilitadas')"
                :options="getComboPriceTables"
                :multiple="true"
                :clearable="false"
                :placeholder="$t('Selecione as tabelas de preço')"
                validation="required"
                validation-behavior="live"
              />
            </b-col> -->
          </b-row>

          <b-row class="mt-3">
            <b-col md="12">
              <h5>{{ $t('Regulamento e Termos de Uso') }}</h5>
              <p class="text-muted">
                {{ $t('Configure os termos e condições do programa de recompensas') }}
              </p>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="4">
              <FormulateInput
                id="reward_config-terms_version"
                v-model="configuration.termsVersion"
                type="text"
                :label="$t('Versão do Regulamento')"
                :placeholder="$t('Ex: 1.0, 2.1, etc.')"
                validation="required|max:15"
                validation-behavior="live"
                :help="$t('Máximo 15 caracteres')"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <FormulateInput
                id="reward_config-terms_and_conditions"
                v-model="configuration.termsAndConditions"
                type="rich-editor"
                :label="$t('Termos e Condições')"
                :placeholder="
                  $t('Digite aqui o regulamento completo do programa de recompensas...')
                "
                validation="required"
                validation-behavior="live"
                :help="$t('Este texto será exibido para os usuários ao fazer opt-in no programa')"
              />
            </b-col>
          </b-row>

          <!-- Configurações por Tabela de Preço -->
          <b-row class="mt-3">
            <b-col md="12">
              <h5>{{ $t('Configurações por Tabela de Preço') }}</h5>
              <p class="text-muted">
                {{ $t('Configure percentuais específicos para cada tabela de preço') }}
              </p>
            </b-col>
          </b-row>

          <b-row
            v-for="(setting, index) in configuration.priceTableSettings"
            :key="setting.priceTableId"
            class="mb-2"
          >
            <b-col md="3">
              <FormulateInput
                :id="`price_table_${index}`"
                v-model="setting.priceTableId"
                type="vue-select"
                :label="index === 0 ? $t('Tabela de Preço') : ''"
                :options="getComboPriceTables"
                :clearable="false"
                :placeholder="$t('Selecione')"
                validation="required"
                validation-behavior="live"
                @input="onPriceTableChange(index, $event)"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                :id="`cashback_percentage_${index}`"
                v-model="setting.cashbackPercentage"
                type="number"
                :label="index === 0 ? $t('Cashback (%)') : ''"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`credit_validity_days_${index}`"
                v-model="setting.creditValidityDays"
                type="number"
                :label="index === 0 ? $t('Validade (dias)') : ''"
                :min="1"
                validation="required|min:1"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`credit_usage_limit_percentage_${index}`"
                v-model="setting.creditUsageLimitPercentage"
                type="number"
                :label="index === 0 ? $t('Limite de Uso por Venda (%)') : ''"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <div :class="{ 'mt-4': index === 0 }">
                <e-button
                  :id="`remove_price_table_${index}`"
                  type="button"
                  variant="outline-danger"
                  icon="trash-2"
                  size="sm"
                  @click="removePriceTableSetting(index)"
                />
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <e-button
                id="add_price_table_setting"
                type="button"
                variant="outline-primary"
                icon="plus"
                :text="$t('Adicionar Tabela de Preço')"
                @click="addPriceTableSetting"
              />
            </b-col>
          </b-row>

          <!-- Períodos Especiais -->
          <b-row class="mt-3">
            <b-col md="12">
              <h5>{{ $t('Períodos Especiais') }}</h5>
              <p class="text-muted">
                {{ $t('Configure períodos específicos com cashback diferenciado') }}
              </p>
            </b-col>
          </b-row>

          <b-row
            v-for="(period, index) in configuration.specialPeriods"
            :key="`period_${index}`"
            class="mb-2"
          >
            <b-col md="2">
              <FormulateInput
                :id="`period_day_${index}`"
                v-model="period.dayOfWeek"
                type="vue-select"
                :label="index === 0 ? $t('Dia da Semana') : ''"
                :options="dayOfWeekOptions"
                :clearable="false"
                :placeholder="$t('Selecione')"
                validation="required"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`period_start_${index}`"
                v-model="period.startTime"
                type="time"
                :label="index === 0 ? $t('Horário Início') : ''"
                validation="required"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`period_end_${index}`"
                v-model="period.endTime"
                type="time"
                :label="index === 0 ? $t('Horário Fim') : ''"
                validation="required"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`period_cashback_${index}`"
                v-model="period.cashbackPercentage"
                type="number"
                :label="index === 0 ? $t('Cashback (%)') : ''"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`period_description_${index}`"
                v-model="period.description"
                type="text"
                :label="index === 0 ? $t('Descrição') : ''"
                :placeholder="$t('Ex: Happy Hour')"
              />
            </b-col>
            <b-col md="1">
              <FormulateInput
                :id="`period_active_${index}`"
                v-model="period.isActive"
                type="switch"
                :label="index === 0 ? $t('Ativo') : ''"
              />
            </b-col>
            <b-col md="1">
              <div :class="{ 'mt-4': index === 0 }">
                <e-button
                  :id="`remove_period_${index}`"
                  type="button"
                  variant="outline-danger"
                  icon="trash-2"
                  size="sm"
                  @click="removeSpecialPeriod(index)"
                />
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <e-button
                id="add_special_period"
                type="button"
                variant="outline-primary"
                icon="plus"
                :text="$t('Adicionar Período Especial')"
                @click="addSpecialPeriod"
              />
            </b-col>
          </b-row>

          <!-- Produtos Especiais -->
          <b-row class="mt-3">
            <b-col md="12">
              <h5>{{ $t('Produtos Especiais') }}</h5>
              <p class="text-muted">
                {{ $t('Configure produtos específicos com cashback adicional') }}
              </p>
            </b-col>
          </b-row>

          <b-row
            v-for="(product, index) in configuration.specialProducts"
            :key="`product_${index}`"
            class="mb-2"
          >
            <b-col md="3">
              <e-search-sku
                :id="`product_id_${index}`"
                v-model="product.sku"
                value-is-object
                :is-credit-kit="false"
                autofocus
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`product_quantity_${index}`"
                v-model="product.minimumPurchaseQuantity"
                type="number"
                :label="index === 0 ? $t('Qtd Mínima') : ''"
                :step="0.001"
                :min="0"
                :placeholder="$t('Opcional')"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`product_cashback_${index}`"
                v-model="product.cashbackPercentage"
                type="number"
                :label="index === 0 ? $t('Cashback (%)') : ''"
                :step="0.01"
                :min="0"
                :max="100"
                validation="required|min:0|max:100"
                validation-behavior="live"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`product_start_${index}`"
                v-model="product.startDate"
                type="date"
                :label="index === 0 ? $t('Data Início') : ''"
                :placeholder="$t('Opcional')"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                :id="`product_end_${index}`"
                v-model="product.endDate"
                type="date"
                :label="index === 0 ? $t('Data Fim') : ''"
                :placeholder="$t('Opcional')"
              />
            </b-col>
            <b-col md="1">
              <FormulateInput
                :id="`product_active_${index}`"
                v-model="product.isActive"
                type="switch"
                :label="index === 0 ? $t('Ativo') : ''"
              />
            </b-col>
            <b-col md="1">
              <div :class="{ 'mt-4': index === 0 }">
                <e-button
                  :id="`remove_product_${index}`"
                  type="button"
                  variant="outline-danger"
                  icon="trash-2"
                  size="sm"
                  @click="removeSpecialProduct(index)"
                />
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <e-button
                id="add_special_product"
                type="button"
                variant="outline-primary"
                icon="plus"
                :text="$t('Adicionar Produto Especial')"
                @click="addSpecialProduct"
              />
            </b-col>
          </b-row>
        </template>

        <b-row class="mt-3">
          <b-col
            md="12"
            align="right"
          >
            <e-button
              id="cancel_reward_config"
              type="button"
              class="mr-2"
              variant="outline-secondary"
              icon="x"
              :text="$t('Cancelar')"
              @click="$router.go(-1)"
            />
            <e-button
              id="save_reward_config"
              type="submit"
              variant="primary"
              icon="save"
              :text="$t('Salvar')"
            />
          </b-col>
        </b-row>
      </FormulateForm>
    </b-card-actions>
  </section>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import saleDomain from '@/mixins/dominio/sale-domain'
import alerts from '@/mixins/alerts'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { EButton, ESearchSku } from '@/views/components'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: { BCardActions, BRow, BCol, EButton, ESearchSku },
  mixins: [saleDomain, alerts],
  data() {
    return {
      loading: false,
      configuration: {
        id: null,
        isActive: false,
        isRedemptionActive: true,
        globalCashbackPercentage: 1.0,
        globalCreditValidityDays: 90,
        globalCreditUsageLimitPercentage: 50,
        requireAppRegistration: false,
        minimumPurchaseValue: null,
        enabledSaleClassifications: [],
        priceTableSettings: [],
        specialPeriods: [],
        specialProducts: [],
        termsAndConditions: '',
        termsVersion: '',
      },
      productOptions: [],
      dayOfWeekOptions: [
        { value: 1, label: 'Segunda-feira' },
        { value: 2, label: 'Terça-feira' },
        { value: 3, label: 'Quarta-feira' },
        { value: 4, label: 'Quinta-feira' },
        { value: 5, label: 'Sexta-feira' },
        { value: 6, label: 'Sábado' },
        { value: 7, label: 'Domingo' },
      ],
    }
  },
  computed: {
    ...mapGetters('common/priceTables', ['getComboPriceTables']),
    saleClassificationOptions() {
      return [
        { value: 'E1', label: this.$t('Normal (E1)') },
        { value: 'E2', label: this.$t('Eventos (E2)') },
        { value: 'E3', label: this.$t('Outros (E3)') },
        { value: 'E4', label: this.$t('Comercial (E4)') },
        { value: 'E5', label: this.$t('Comercial Loja (E5)') },
      ]
    },
  },
  mounted() {
    this.loadConfiguration()
    this.loadInitialProducts()
    this.fetchPriceTables()
  },
  methods: {
    ...mapActions('common/priceTables', ['fetchPriceTables']),
    async loadConfiguration() {
      try {
        this.loading = true
        const response = await this.$http.get(`/api/reward-configuration`)
        if (response.data) {
          this.configuration = {
            ...this.configuration,
            ...response.data,
            specialPeriods: response.data.specialPeriods || [],
            specialProducts:
              response.data.specialProducts?.map(product => ({
                ...product,
                sku: { id: product.skuId },
              })) || [],
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          this.showError({
            title: this.$t('Erro'),
            message: this.$t('Erro ao carregar configuração de recompensas'),
          })
        }
      } finally {
        this.loading = false
      }
    },
    async saveConfiguration() {
      try {
        this.loading = true

        await this.$http.put(`/api/reward-configuration`, {
          ...this.configuration,
          specialProducts: this.configuration.specialProducts.map(product => ({
            ...product,
            skuId: product.sku?.id || product.skuId,
          })),
        })

        this.showSuccess({
          title: this.$t('Sucesso'),
          message: this.$t('Configuração salva com sucesso!'),
        })

        this.$router.go(-1)
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loading = false
      }
    },
    addPriceTableSetting() {
      this.configuration.priceTableSettings.push({
        priceTableId: null,
        cashbackPercentage: this.configuration.globalCashbackPercentage,
        creditValidityDays: this.configuration.globalCreditValidityDays,
        creditUsageLimitPercentage: this.configuration.globalCreditUsageLimitPercentage,
      })
    },
    removePriceTableSetting(index) {
      this.configuration.priceTableSettings.splice(index, 1)
    },
    onPriceTableChange(index, priceTableId) {
      // Verifica se a tabela de preço já foi selecionada
      const existing = this.configuration.priceTableSettings.find(
        (setting, i) => i !== index && setting.priceTableId === priceTableId
      )

      if (existing) {
        this.showWarning({
          title: this.$t('Atenção'),
          message: this.$t('Esta tabela de preço já foi selecionada'),
        })
        this.configuration.priceTableSettings[index].priceTableId = null
      }
    },
    addSpecialPeriod() {
      this.configuration.specialPeriods.push({
        dayOfWeek: 1,
        startTime: '09:00',
        endTime: '18:00',
        cashbackPercentage: 2.0,
        isActive: true,
        description: '',
      })
    },
    removeSpecialPeriod(index) {
      this.configuration.specialPeriods.splice(index, 1)
    },
    addSpecialProduct() {
      this.configuration.specialProducts.push({
        skuId: null,
        minimumPurchaseQuantity: null,
        cashbackPercentage: 3.0,
        startDate: null,
        endDate: null,
        isActive: true,
        description: '',
      })
    },
    removeSpecialProduct(index) {
      this.configuration.specialProducts.splice(index, 1)
    },
    async searchProducts(search) {
      if (search.length < 2) return

      try {
        const response = await this.$http.get('/api/skus', {
          params: {
            name: search,
            pageIndex: 0,
            pageSize: 50,
            active: true,
          },
        })

        this.productOptions = response.data.results.map(sku => ({
          value: sku.id,
          label: `${sku.product.name} - ${sku.name} (${sku.id})`,
        }))
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
      }
    },
    async loadInitialProducts() {
      // Carrega produtos iniciais se houver produtos especiais já configurados
      if (this.configuration.specialProducts?.length) {
        const productIds = this.configuration.specialProducts.map(p => p.skuId).filter(Boolean)
        if (productIds.length) {
          try {
            // Buscar SKUs por IDs individuais
            const skuPromises = productIds.map(id =>
              this.$http.get(`/api/skus/${id}`).catch(() => null)
            )

            const responses = await Promise.all(skuPromises)
            const skus = responses
              .filter(response => response?.data)
              .map(response => response.data)

            this.productOptions = skus.map(sku => ({
              value: sku.id,
              label: `${sku.product.name} - ${sku.name} (${sku.id})`,
            }))
          } catch (error) {
            console.error('Erro ao carregar produtos iniciais:', error)
          }
        }
      }
    },
  },
}
</script>
